import React, { useEffect } from 'react';
import styled, { keyframes } from 'styled-components';
import { logEvent, getAnalytics } from 'firebase/analytics';
import { useTranslation, Trans } from 'react-i18next';
import { themeColor } from '../Theming';
import { eonImages } from '../Theming/images';
import { useNavigate } from 'react-router-dom';
import { Path } from '../Routing';
import app from '../Firebase';
import { usePageViewTracking, useQuery } from '../Common/Hooks';
import Header from './Components/Header';
import {
  ANALYTICS_ACTION_LABELS,
  ANALYTICS_PAGE_NAMES,
  trackActivationActionEvent,
} from '../Common/Analytics/utils';

const Container = styled.div`
  flex: 1;
  flex-direction: column;
  display: flex;
  height: 100dvh;
  max-width: 600px;
  margin: 0 auto;
  display: relative;
`;

export const HyperisePlaceholderContainer = styled.div`
  width: 100%;
  height: fit-content;
`;

const bounce = keyframes`
  0%, 20%, 50%, 80%, 100% {
    transform: translateY(0);
  }
  40% {
    transform: translateY(-20px);
  }
  60% {
    transform: translateY(-10px);
  }
`;

const StyledImage = styled.img`
  position: fixed;
  top: 4vh;
  right: 20px;
  animation: ${bounce} 2s infinite;
  height: 8vh;
`;

const Title = styled.div`
  margin-top: 10vh;
  & p {
    text-align: center;
    color: ${themeColor('white')};
    font-size: 28px;
    line-height: 35px;
    margin: 0;
    font-family: Helvetica;
    padding-inline: 8vw;

    :last-child {
      margin-top: 5px;
      font-weight: 800;
    }
  }
`;

const MainContent = styled.div`
  padding-top: 15vh;
  flex: 1;
  display: flex;
  flex-direction: column;

  & > button {
    background-color: ${themeColor('navyBlueText')};
    height: 56px;
    margin: 50px 35px 0 35px;
    border-radius: 100px;
    color: ${themeColor('white')};
    font-size: 16px;
    font-weight: 700;
    font-family: Helvetica;
    border: none;
  }
`;

const MainText = styled.p`
  margin: 0;
  padding: 0;
  color: ${themeColor('navyBlueText')};
  padding-inline: 5vw;
  font-size: 20px;
  text-align: center;
  font-family: Helvetica;

  & > span {
    font-weight: 700;
  }
`;

interface Message {
  data: string & { type: string; givenPermission?: boolean };
}

const EonPluginActivationAlwaysAllow = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { additional_permissions_page } = ANALYTICS_PAGE_NAMES;
  const { activation_permissions } = ANALYTICS_ACTION_LABELS;
  usePageViewTracking(additional_permissions_page, 'Eon');
  const query = useQuery();

  useEffect(() => {
    const analytics = getAnalytics(app);

    const messageHandler = (message: Message) => {
      if (message && message.data === '') {
        window.location.reload();
      } else if (message) {
        switch (message.data?.type) {
          case 'PluginEnabled': {
            trackActivationActionEvent(
              query,
              'Eon',
              activation_permissions,
              additional_permissions_page,
              {
                permissions: message.data?.givenPermission ? 'Full' : 'Partial',
              }
            );
            logEvent(analytics, 'activation_permissions', {
              permissions: message.data?.givenPermission ? 'Full' : 'Partial',
              partner: 'Eon',
            });

            if (message.data?.givenPermission) {
              navigate({
                pathname: Path.EonPluginActivationStartShopping,
                search: location.search,
              });
            } else {
              navigate({
                pathname: Path.EonPluginActivationPartialPermissions,
                search: location.search,
              });
            }
            break;
          }
        }
      }
    };

    window.addEventListener('message', messageHandler);

    return () => window.removeEventListener('message', messageHandler);
  }, [navigate]);

  return (
    <Container>
      <StyledImage src={eonImages.safari_tap_review} />
      <Header
        height="40vh"
        titleContent={
          <Title>
            <Trans t={t} i18nKey="eon.youreAlmostThere">
              <p />
              <p />
            </Trans>
          </Title>
        }
      />
      <MainContent>
        <MainText>
          <Trans t={t} i18nKey="eon.selectAlwaysAllow">
            <span />
          </Trans>
        </MainText>
        <button>{t('eon.alwaysAllow')}</button>
      </MainContent>
    </Container>
  );
};

export default EonPluginActivationAlwaysAllow;
